import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import React, { lazy, startTransition, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Task, TaskType } from '@amzn/taskscheduling-service';
import { Action, Resource } from '@amzn/fae-auth-service';
import { useNavigate } from 'react-router-dom';
import { columnDefinitions } from 'src/shadow-pnl-allocations/pages/manage-slice-allocation-page/config/table-config';
import { filteringProperties } from 'src/shadow-pnl-allocations/pages/manage-slice-allocation-page/config/filter-property';
import { getContentDisplayOptions } from 'src/common/utils/table';
import { Table } from 'src/common/components/Table';
import QueryKey from 'src/api/QueryKey';
import { TaskSchedulingServiceApi } from 'src/api/TaskSchedulingServiceApi';
import { getTasksWithExecutionState } from 'src/common/utils/task';
import { TaskWithStatus } from 'src/common/types/TaskWithStatus';
import { AuthServiceApi } from 'src/api/AuthServiceApi';

const createExecuteDriverModal = () =>
  lazy(
    () =>
      import(
        'src/shadow-pnl-allocations/pages/manage-slice-allocation-page/ExecuteAllocationModal'
      ),
  );

const ExecuteDriverModal = createExecuteDriverModal();

const createPermissionQuery = (action: Action, item?: Task) => ({
  queryKey: [QueryKey.HasPermission, action, item],
  queryFn: () => {
    const input = item?.input?.slicedAllocationTaskInput;
    return AuthServiceApi.hasPermissions({
      action,
      resource: Resource.TASK,
      scenario: input?.scenario,
    });
  },
  enabled: Boolean(item),
});

const usePermissionQuery = (action: Action, item?: Task) =>
  useQuery(createPermissionQuery(action, item));

interface ManageFCFixedDriverPageProps {
  disableDeleteButton?: boolean;
}

const ManageFCFixedDriverPage: React.FC<ManageFCFixedDriverPageProps> = ({
  disableDeleteButton = true,
}) => {
  const { t } = useTranslation();
  const columnDef = useMemo(() => columnDefinitions(t), [t]);
  const navigate = useNavigate();

  const sortingColumn = columnDef.find((def) => def.id === 'creationDate');

  const [selectedItems, setSelectedItems] = useState<Task[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [executeModal, setExecuteModal] = useState(false);

  const tasks = useQuery({
    queryKey: [
      QueryKey.ListTasksWithLatestExecutionState,
      TaskType.FAE_SLICED_ALLOCATIONS_TASK,
      TaskType.FAE_FC_FIXED_ALLOCATIONS_TASK,
    ],
    queryFn: () =>
      TaskSchedulingServiceApi.listTaskWithLatestExecution({
        taskTypes: [
          TaskType.FAE_SLICED_ALLOCATIONS_TASK,
          TaskType.FAE_FC_FIXED_ALLOCATIONS_TASK,
        ],
      }),
    select: (data) =>
      getTasksWithExecutionState(data.taskWithExecutionStateList ?? []).filter(
        (task) => task.name?.startsWith('FCFIXED_DRIVER'),
      ),
  });

  const hasDeletePermission = usePermissionQuery(
    Action.DELETE,
    selectedItems[0],
  );

  const hasExecutePermission = usePermissionQuery(
    Action.EXECUTE,
    selectedItems[0],
  );

  const hasUpdatePermission = usePermissionQuery(
    Action.UPDATE,
    selectedItems[0],
  );

  const handleUpdate = () => {
    navigate(`/fcfixeddriver/update/${selectedItems[0].taskId}`);
  };

  const toggleDeleteModal = () => {
    startTransition(() => setDeleteModal(!deleteModal));
  };

  const toggleExecuteModal = () => {
    startTransition(() => setExecuteModal(!executeModal));
  };

  const navigateToDriverDetails = () => {
    navigate(`/driver/details/${selectedItems[0].taskId}`);
  };

  return (
    <div data-testid="manage-fcfixed-driver-page">
      {executeModal && (
        <ExecuteDriverModal
          visible={executeModal}
          onClose={toggleExecuteModal}
          allocation={selectedItems[0]}
        />
      )}
      <Table<TaskWithStatus>
        variant="full-page"
        loading={tasks.isFetching}
        trackBy="taskId"
        testid="manage-fcfixed-driver-table"
        data={tasks.data ?? []}
        header={t('manage_driver')}
        columnDefinitions={columnDef}
        contentDisplayOptions={getContentDisplayOptions(columnDef)}
        selectedItems={selectedItems}
        onSelectionChange={setSelectedItems}
        pagination={true}
        preferences={true}
        empty={{
          title: t('empty_driver'),
          description: t('empty_driver_description'),
        }}
        selectionType="single"
        filteringProperties={filteringProperties(t)}
        sortingState={
          sortingColumn && {
            sortingColumn,
            isDescending: true,
          }
        }
        buttons={
          <SpaceBetween direction="horizontal" size="s">
            <Button
              disabled={
                !selectedItems.length || !hasUpdatePermission.data?.isAuthorized
              }
              loading={hasUpdatePermission.isFetching}
              data-testid="update-fcfixed-driver"
              onClick={handleUpdate}
            >
              {t('update')}
            </Button>
            <Button
              disabled={
                disableDeleteButton ||
                !selectedItems.length ||
                !hasDeletePermission.data?.isAuthorized
              }
              loading={hasDeletePermission.isFetching}
              data-testid="delete-fcfixed-driver"
              onClick={toggleDeleteModal}
            >
              {t('delete')}
            </Button>
            <Button
              disabled={!selectedItems.length}
              data-testid="fcfixed-driver-history"
              onClick={navigateToDriverDetails}
            >
              {t('execution_details')}
            </Button>
            <Button
              disabled={
                !selectedItems.length ||
                !hasExecutePermission.data?.isAuthorized
              }
              loading={hasExecutePermission.isFetching}
              data-testid="execute-fcfixed-driver"
              onClick={toggleExecuteModal}
              variant="primary"
            >
              {t('execute')}
            </Button>
          </SpaceBetween>
        }
      />
    </div>
  );
};

export default ManageFCFixedDriverPage;
